import { StatusOfSpecialistEmailHeaderJob } from '@agingplan/src/lib/interfaces/job-batch-events';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { Toast, ToastContainerService } from 'src/app/services/toast-container.service';
import { WebsocketService } from 'src/app/services/websocket.service';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class EmailHeaderService {
  private timeoutId: ReturnType<typeof setTimeout> | null = null;
  private toast: Toast | null = null;

  public is_xml_file_processing$ = new BehaviorSubject<boolean>(false);
  public is_email_header_merge_process_complete$ = new BehaviorSubject<boolean>(false);

  public processingStatus: ProcessingStatus = {
    total: 0,
    completed: 0,
    remaining: 0,
    progressbar: 0,
  };

  constructor(
    private http: HttpClient,
    private toastContainerService: ToastContainerService,
    private websocketService: WebsocketService,
  ) {}

  /**
   * Watch for email template errors
   */
  public watchForEmailTemplateErrors(stop: boolean = false): void {
    if (stop) {
      this.websocketService.unsubscribeFromChannel('email_builder-error');
      return;
    }

    this.websocketService.subscribeToEmailBuilderErrors();
  }

  /**
   * Update processing status for toast notification
   * @param {StatusOfSpecialistEmailHeaderJob} data -  The received processing data.
   */
  private updateProcessingStatus(data: StatusOfSpecialistEmailHeaderJob): void {
    this.processingStatus.remaining = (data.pending_jobs - 1) ?(data.pending_jobs - 1): 0;
    this.processingStatus.completed = (data.total_jobs - data.pending_jobs + 1) ?(data.total_jobs - data.pending_jobs + 1): 0;
    this.processingStatus.total = data.total_jobs ?? 0;
    this.processingStatus.completed = Math.min(this.processingStatus.completed, this.processingStatus.total);
    this.processingStatus.progressbar = data.process_percentage ?? 0;
    if (this.processingStatus.remaining <= 0) {
      this.processingStatus.remaining = 0;
      this.setCompletionTimeout();
    }

    if (this.processingStatus.progressbar >= 100) {
      this.is_email_header_merge_process_complete$.next(false);
    }

    this.showToast(
      'Email Header Processing',
      `Total: ${this.processingStatus.total}, Completed: ${this.processingStatus.completed}, Remaining: ${this.processingStatus.remaining}`,
      this.processingStatus.progressbar,
    );
  }

  /**
   * listen for last processing data and set process complete
   */
  private setCompletionTimeout(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }

    this.timeoutId = setTimeout(() => {
      if (this.processingStatus.remaining === 0) {
        this.processingStatus.progressbar = 100;
        this.is_email_header_merge_process_complete$.next(true);
      }
    }, 2000);
  }

  /**
   * Show toast notification
   * @param {string} header -  The toast header message.
   * @param {string} message -  The toast message.
   * @param {number} process_percentage -  show progress bar or not.
   */
  public showToast(header: string, message: string, process_percentage: number = 0): void {
    if (!this.toast) this.toast = this.toastContainerService.present(header, message);
    const toast = this.toastContainerService.getById(this.toast.id);
    if (!toast) this.toast = this.toastContainerService.present(header, message);
    this.toastContainerService.updateProgressBar(this.toast.id, process_percentage, message);
  }

  /**
   * Monitor status for email header job
   * @param {number} ltciSpecialistId -  The ltci specialist id.
   */
  public monitorStatusOfSpecialistEmailHeaderJob(ltciSpecialistId: number): void {
    try {
      this.websocketService.subscribeToEmailHeaderStatus(ltciSpecialistId, (data: StatusOfSpecialistEmailHeaderJob) => {
        if (data) {
          this.updateProcessingStatus(data);
        }
      });
    } catch (error) {
      this.showToast('Error', 'An error occurred while setting up email header merge.');
    }
  }

  /**
   * Generate Email Header Process
   * @param {number} ltci_specialist_agentID -  The ltci specialist agentID.
   */
  public async generateEmailHeader(ltci_specialist_agentID: number): Promise<StatusOfSpecialistEmailHeaderJob | null> {
    try {
      const response = await firstValueFrom(this.http.post<StatusOfSpecialistEmailHeaderJob>(`${environment.api_url}/email/email-builder-export`, { ltci_specialist_agentID }));
      if (response) {
        this.monitorStatusOfSpecialistEmailHeaderJob(response.ltci_specialist_agentID || ltci_specialist_agentID);
      }
      return response;
    } catch (error) {
      console.error('Error generating email header:', error);
      this.showToast('Error', 'An error occurred while generating email header.');
      return null;
    }
  }
}
interface ProcessingStatus {
  total: number;
  completed: number;
  remaining: number;
  progressbar: number;
}
